import type { Message } from '@/types';
import { normalizeForDedupe } from '@/lib/utils';

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'document', 'sticker']);
const WINDOW_MS = 10_000;
type Bounds = { min: number; max: number };
type Bucket = { all?: Bounds; text?: Bounds };

/** Same visual rule as ChatArea: kept messages only, strict <10s, never media/media.
 * Time buckets index both neighbors, so unsorted inputs retain the same semantics.
 * One normalization and constant bucket probes per message; no payload projection.
 */
export function deduplicateVisualMessages(messages: Message[]): Message[] {
  const groups = new Map<string, Map<number, Bucket>>();
  const result: Message[] = [];
  for (const msg of messages) {
    const time = new Date(msg.created_at).getTime();
    if (!Number.isFinite(time)) { result.push(msg); continue; }
    const key = JSON.stringify([normalizeForDedupe(msg.content),
      ...[msg.message_direction, msg.message_sender_type, msg.remotejid].map(v => [typeof v, v])]);
    let buckets = groups.get(key);
    if (!buckets) { buckets = new Map(); groups.set(key, buckets); }
    const slot = Math.floor(time / WINDOW_MS);
    const media = MEDIA_TYPES.has(msg.message_type);
    let duplicate = false;
    for (let index = slot - 1; index <= slot + 1; index++) {
      const bucket = buckets.get(index);
      const bounds = media ? bucket?.text : bucket?.all;
      if (bounds && bounds.max > time - WINDOW_MS && bounds.min < time + WINDOW_MS) {
        duplicate = true; break;
      }
    }
    if (duplicate) continue;
    result.push(msg);
    let bucket = buckets.get(slot);
    if (!bucket) { bucket = {}; buckets.set(slot, bucket); }
    const extend = (bounds?: Bounds): Bounds => bounds
      ? { min: Math.min(bounds.min, time), max: Math.max(bounds.max, time) }
      : { min: time, max: time };
    bucket.all = extend(bucket.all);
    if (!media) bucket.text = extend(bucket.text);
  }
  return result;
}
